/**
 * Service for managing user browser credentials.
 *
 * Handles storage, retrieval, and management of user-specific browser automation credentials.
 */

import { apiClient } from './apiClient';

export interface UserBrowserCredentialData {
  platform_id: number;
  credentials: Record<string, any>;
}

export interface UserBrowserCredentialStatus {
  exists: boolean;
  stored_at?: string;
  ttl_seconds?: number;
  ttl_minutes?: number;
}

export interface UserBrowserCredentialInfo {
  platform_id: number;
  platform_name?: string;
  platform_type?: string;
  stored_at?: string;
  ttl_seconds?: number;
  ttl_minutes?: number;
}

export interface StoreUserBrowserCredentialResponse {
  status: string;
  message: string;
  expires_at: string;
}

export interface ListUserBrowserCredentialsResponse {
  credentials: UserBrowserCredentialInfo[];
}

/**
 * Service for managing user browser credentials
 */
export class UserBrowserCredentialService {
  /**
   * Store user browser credentials for a platform
   */
  static async storeCredentials(
    platformId: number,
    credentials: Record<string, any>
  ): Promise<StoreUserBrowserCredentialResponse> {
    const response = await apiClient.post('/api/store-user-browser-credentials/', {
      platform_id: platformId,
      credentials
    });

    return response.data;
  }

  /**
   * Check if user has existing browser credentials for a platform
   */
  static async checkCredentials(platformId: number): Promise<UserBrowserCredentialStatus> {
    const response = await apiClient.get(`/api/check-user-browser-credentials/${platformId}/`);
    return response.data;
  }

  /**
   * List all browser credentials for the current user
   */
  static async listCredentials(): Promise<ListUserBrowserCredentialsResponse> {
    const response = await apiClient.get('/api/user-browser-credentials/');
    return response.data;
  }

  /**
   * Delete user browser credentials for a platform
   */
  static async deleteCredentials(platformId: number): Promise<{ status: string; message: string }> {
    const response = await apiClient.delete(`/api/user-browser-credentials/${platformId}/`);
    return response.data;
  }

  /**
   * Check if credentials exist and are still valid (TTL > 0)
   */
  static credentialsValid(status: UserBrowserCredentialStatus): boolean {
    return status.exists && (status.ttl_seconds || 0) > 0;
  }

  /**
   * Get remaining time in minutes for credentials
   */
  static getRemainingMinutes(status: UserBrowserCredentialStatus): number {
    return status.ttl_minutes || 0;
  }

  /**
   * Check if credentials are about to expire (less than 5 minutes)
   */
  static credentialsExpiringSoon(status: UserBrowserCredentialStatus): boolean {
    return this.credentialsValid(status) && this.getRemainingMinutes(status) < 5;
  }
}